import {XIcon, CheckIcon, ChevronDownIcon} from '@primer/octicons-react'
import {Box, Button, Grid, Heading, Image, Label, SectionIntro, Stack, Text} from '@primer/react-brand'
import {allFeatures} from './PricingData'
import type {Feature, FeatureGroup} from './PricingData'
import {useState} from 'react'

interface PricingGroupedProps {
  copilotSignupPath: string
  copilotForBusinessSignupPath: string
  copilotContactSalesPath: string
}

export function PricingGrouped({
  copilotSignupPath,
  copilotForBusinessSignupPath,
  copilotContactSalesPath,
}: PricingGroupedProps) {
  const copilotBusinessContactSalesPath = `${copilotContactSalesPath}&utm_content=CopilotBusiness`
  const copilotEnterpriseContactSalesPath = `${copilotContactSalesPath}&utm_content=CopilotEnterprise`

  const [expandedStates, setExpandedStates] = useState<{[key: string]: boolean}>({})
  const isDetailsExpanded = (id: string) => {
    return expandedStates[id]
  }

  const toggleDetailsExpanded = (e: React.MouseEvent<HTMLElement>) => {
    const id = (e.target as HTMLElement).getAttribute('data-target-id')
    if (!id) return
    setExpandedStates(prev => ({...prev, [id]: !prev[id]}))
  }

  return (
    <div>
      <Grid className="lp-Section-container--centerUntilMedium lp-Grid--noRowGap">
        <Grid.Column span={12} className="z-1">
          <SectionIntro fullWidth className="lp-SectionIntro">
            <SectionIntro.Label size="large" className="lp-Label--section">
              Pricing
            </SectionIntro.Label>
            <SectionIntro.Heading size="1" weight="bold">
              Take flight with GitHub Copilot.
            </SectionIntro.Heading>
            <SectionIntro.Description className="lp-SectionIntro-description--wide">
              Organizations and developers all over the world use GitHub Copilot to code faster, drive impact, and focus
              on doing what matters most: building great software.
            </SectionIntro.Description>
          </SectionIntro>
        </Grid.Column>

        <Grid.Column span={12} className="position-relative">
          <Image
            className="lp-Pricing-bgGlow m-auto top-n7 top-md-n9 left-0 right-0 position-absolute col-11 col-md-6 height-auto events-none"
            src="/images/modules/site/copilot/pricing-glow.webp"
            loading="lazy"
            width="50"
            height="62"
            alt=""
          />

          <Grid className="lp-Pricing">
            <Grid.Column span={12} className="lp-Pricing-item px-0 px-md-4 py-0 has-BlurredBg has-GradientBorder">
              <Stack direction={{narrow: 'vertical', regular: 'horizontal'}} gap={32} padding="none">
                {/* Copilot Individual */}
                <Stack padding="none" gap={24} style={{flex: '1'}} className="pt-4 pt-md-6 pb-1 pb-md-9 px-4 px-md-0">
                  <Box>
                    <Label size="medium" color="purple-red" className="d-none d-md-block" style={{opacity: 0}}>
                      &nbsp;
                    </Label>
                    <Box marginBlockStart={16} marginBlockEnd={16}>
                      <Heading as="h3" size="5">
                        Copilot Individual
                      </Heading>
                    </Box>
                    <Text as="p" size="200" weight="normal" variant="muted" className="lp-Pricing-description--org">
                      For individual developers, freelancers, students, and educators that want to code faster and
                      happier.
                    </Text>
                  </Box>

                  <Box>
                    <Stack direction="horizontal" gap={12} padding="none" className="lp-Pricing-price">
                      <Text size="500" weight="normal" style={{lineHeight: 1.4}} className="is-sansSerifAlt">
                        $
                      </Text>
                      <Text weight="normal" className="lp-Pricing-nr is-sansSerifAlt">
                        10
                      </Text>
                      <Text size="500" weight="normal" className="is-sansSerifAlt" style={{alignSelf: 'end'}}>
                        USD
                      </Text>
                    </Stack>

                    <Text size="100" weight="normal" variant="muted" className="d-block mt-2">
                      per month / $100 USD per year
                    </Text>
                  </Box>

                  <Stack
                    direction={{narrow: 'vertical', wide: 'vertical'}}
                    gap={12}
                    padding="none"
                    className="lp-Pricing-pricing-ctas"
                  >
                    <Button as="a" href={copilotSignupPath} block variant="primary">
                      Start a free trial
                    </Button>

                    <Text variant="muted" className="d-block lp-Pricing-disclaimer f6-mktg">
                      Free for verified students, teachers, and maintainers of popular open source projects.
                    </Text>
                  </Stack>

                  <hr className="lp-Pricing-divider" />

                  <div className="text-semibold mb-0 mb-md-3 d-none d-md-block">What&apos;s included</div>
                  <button
                    type="button"
                    aria-expanded={isDetailsExpanded('individual')}
                    data-target-id="individual"
                    className="position-relative text-semibold lp-Pricing-features-toggle-btn mb-0 mb-md-3 d-md-none"
                    onClick={toggleDetailsExpanded}
                  >
                    What&apos;s included
                    <span className="lp-Pricing-features-icon position-absolute top-0 right-0 bottom-0">
                      <ChevronDownIcon />
                    </span>
                  </button>

                  {allFeatures.map((group: FeatureGroup, i) => (
                    <Box key={i} className="lp-Pricing-features-box">
                      <Text size="200" weight="normal" className="lp-Pricing-feature-group-title">
                        {group.title}
                      </Text>
                      <ul className="lp-Pricing-list">
                        {group.features.map((feature: Feature, j) => (
                          <li key={j} className={feature['individual'] ? 'lp-Pricing-list-check' : 'lp-Pricing-list-x'}>
                            {feature['individual'] ? <CheckIcon /> : <XIcon />}
                            {feature['individual'] ? (
                              <span className="sr-only">Included, </span>
                            ) : (
                              <span className="sr-only">Not included, </span>
                            )}
                            {feature['title']}
                          </li>
                        ))}
                      </ul>
                    </Box>
                  ))}
                </Stack>

                <hr className="lp-Pricing-divider lp-Pricing-divider--grouped" />

                {/* Copilot Business */}
                <Stack padding="none" gap={24} style={{flex: '1'}} className="pt-4 pt-md-6 pb-1 pb-md-9 px-4 px-md-0">
                  <Box>
                    <Label size="medium" color="purple-red" className="label-purple-green">
                      Most popular
                    </Label>
                    <Box marginBlockStart={16} marginBlockEnd={16}>
                      <Heading as="h3" size="5">
                        Copilot Business
                      </Heading>
                    </Box>
                    <Text as="p" size="200" weight="normal" variant="muted" className="lp-Pricing-description--org">
                      For organizations ready to improve engineering velocity, code quality, and developer experience.
                    </Text>
                  </Box>

                  <Box>
                    <Stack direction="horizontal" gap={12} padding="none" className="lp-Pricing-price">
                      <Text size="500" weight="normal" style={{lineHeight: 1.4}} className="is-sansSerifAlt">
                        $
                      </Text>
                      <Text weight="normal" className="lp-Pricing-nr is-sansSerifAlt">
                        19
                      </Text>
                      <Text size="500" weight="normal" className="is-sansSerifAlt" style={{alignSelf: 'end'}}>
                        USD
                      </Text>
                    </Stack>

                    <Text size="100" weight="normal" variant="muted" className="d-block mt-2">
                      per user / month
                    </Text>
                  </Box>

                  <Stack
                    direction={{narrow: 'vertical', wide: 'vertical'}}
                    gap={12}
                    padding="none"
                    className="lp-Pricing-pricing-ctas"
                  >
                    <Button as="a" href={copilotForBusinessSignupPath} block variant="primary">
                      Buy now
                    </Button>
                    <Button as="a" href={copilotBusinessContactSalesPath} block variant="secondary">
                      Contact sales
                    </Button>
                  </Stack>

                  <hr className="lp-Pricing-divider" />

                  <div className="text-semibold mb-0 mb-md-3 d-none d-md-block">What&apos;s included</div>
                  <button
                    type="button"
                    aria-expanded={isDetailsExpanded('business')}
                    data-target-id="business"
                    className="position-relative text-semibold lp-Pricing-features-toggle-btn mb-0 mb-md-3 d-md-none"
                    onClick={toggleDetailsExpanded}
                  >
                    What&apos;s included
                    <span className="lp-Pricing-features-icon position-absolute top-0 right-0 bottom-0">
                      <ChevronDownIcon />
                    </span>
                  </button>

                  {allFeatures.map((group: FeatureGroup, i) => (
                    <Box key={i} className="lp-Pricing-features-box">
                      <Text size="200" weight="normal" className="lp-Pricing-feature-group-title">
                        {group.title}
                      </Text>
                      <ul className="lp-Pricing-list">
                        {group.features.map((feature: Feature, j) => (
                          <li key={j} className={feature['business'] ? 'lp-Pricing-list-check' : 'lp-Pricing-list-x'}>
                            {feature['business'] ? <CheckIcon /> : <XIcon />}
                            {feature['business'] ? (
                              <span className="sr-only">Included, </span>
                            ) : (
                              <span className="sr-only">Not included, </span>
                            )}
                            {feature['title']}
                          </li>
                        ))}
                      </ul>
                    </Box>
                  ))}
                </Stack>

                <hr className="lp-Pricing-divider lp-Pricing-divider--grouped" />

                {/* Copilot Enterprise */}
                <Stack padding="none" gap={24} style={{flex: '1'}} className="pt-4 pt-md-6 pb-6 pb-md-9 px-4 px-md-0">
                  <Box>
                    <Label size="medium" color="purple-red" className="label-purple-green">
                      New
                    </Label>
                    <Box marginBlockStart={16} marginBlockEnd={16}>
                      <Heading as="h3" size="5">
                        Copilot Enterprise
                      </Heading>
                    </Box>
                    <Text as="p" size="200" weight="normal" variant="muted" className="lp-Pricing-description--org">
                      For companies looking to customize GitHub Copilot to their organization and infuse AI across the
                      developer workflow.
                    </Text>
                  </Box>

                  <Box>
                    <Stack direction="horizontal" gap={12} padding="none" className="lp-Pricing-price">
                      <Text size="500" weight="normal" style={{lineHeight: 1.4}} className="is-sansSerifAlt">
                        $
                      </Text>
                      <Text weight="normal" className="lp-Pricing-nr is-sansSerifAlt">
                        39
                      </Text>
                      <Text size="500" weight="normal" className="is-sansSerifAlt" style={{alignSelf: 'end'}}>
                        USD
                      </Text>
                    </Stack>

                    <Text size="100" weight="normal" variant="muted" className="d-block mt-2">
                      per user / month
                    </Text>
                  </Box>

                  <Stack
                    direction={{narrow: 'vertical', wide: 'vertical'}}
                    gap={12}
                    padding="none"
                    className="lp-Pricing-pricing-ctas"
                  >
                    <Button as="a" href={copilotEnterpriseContactSalesPath} variant="primary">
                      Contact sales
                    </Button>
                  </Stack>

                  <hr className="lp-Pricing-divider" />

                  <div className="text-semibold mb-0 mb-md-3 d-none d-md-block">What&apos;s included</div>
                  <button
                    type="button"
                    aria-expanded={isDetailsExpanded('enterprise')}
                    data-target-id="enterprise"
                    className="position-relative text-semibold lp-Pricing-features-toggle-btn mb-0 mb-md-3 d-md-none"
                    onClick={toggleDetailsExpanded}
                  >
                    What&apos;s included
                    <span className="lp-Pricing-features-icon position-absolute top-0 right-0 bottom-0">
                      <ChevronDownIcon />
                    </span>
                  </button>

                  {allFeatures.map((group: FeatureGroup, i) => (
                    <Box key={i} className="lp-Pricing-features-box">
                      <Text size="200" weight="normal" className="lp-Pricing-feature-group-title">
                        {group.title}
                      </Text>
                      <ul className="lp-Pricing-list">
                        {group.features.map((feature: Feature, j) => (
                          <li key={j} className={feature['enterprise'] ? 'lp-Pricing-list-check' : 'lp-Pricing-list-x'}>
                            {feature['enterprise'] ? <CheckIcon /> : <XIcon />}
                            {feature['enterprise'] ? (
                              <span className="sr-only">Included, </span>
                            ) : (
                              <span className="sr-only">Not included, </span>
                            )}
                            {feature['title']}
                          </li>
                        ))}
                      </ul>
                    </Box>
                  ))}
                </Stack>
              </Stack>
            </Grid.Column>
          </Grid>
        </Grid.Column>
      </Grid>

      <div className="lp-Pricing-bgCover">
        <Image
          className="lp-Pricing-bgCoverImage"
          src="/images/modules/site/copilot/pricing-cover.svg"
          loading="lazy"
          alt=""
        />
      </div>
    </div>
  )
}

try{ PricingGrouped.displayName ||= 'PricingGrouped' } catch {}